import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import MessageGroup from './message_group';
import Loader from '../loader';
import useInboxChannel from '../../hooks/useInboxChannel';

// メッセージ一覧（DM）コンポーネント
//
// TGW-1033: 130秒ポーリングを撤廃し、MessageInboxChannel を経由した
// WebSocket 経由でリアルタイム更新する。N3 多段防御は useInboxChannel が内蔵。
function MessageGroupList(props) {
  const { messageGroupId, isCompany, companyId } = props;

  const initialData = {
    messageGroups: [],
    currentUserId: 0,
    unreadMessageCount: {},
    lastMessages: {},
    pagenationSettings: {
      currentPage: 0,
      maxPerPage: 0,
      isLastPage: false,
    },
  };

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(initialData);

  const loadMessageGroupListFromServer = useCallback(() => {
    return new Promise((resolve) => {
      const cid = typeof companyId === 'undefined' ? null : companyId;
      $.ajax({
        url: '/api/message/index',
        data: { company_id: cid },
        dataType: 'json',
        cache: false,
        success: (resp) => {
          setLoading(false);
          setData((prev) => ({
            ...prev,
            ...resp,
            // unreadMessageCount / lastMessages が無いレスポンスでも壊れないよう既定値で補完
            unreadMessageCount: resp.unreadMessageCount || {},
            lastMessages: resp.lastMessages || {},
          }));
          resolve();
        },
        error: () => {
          resolve();
        },
      });
    });
  }, [companyId]);

  const loadMoreMessageGroupListFromServer = useCallback(
    (page) => {
      const cid = typeof companyId === 'undefined' ? null : companyId;
      $.ajax({
        url: '/api/message/index',
        data: {
          company_id: cid,
          page: page + 1,
        },
        dataType: 'json',
        cache: false,
        success: (resp) => {
          setLoading(false);
          setData((prev) => {
            const currentGroupIds = prev.messageGroups.map((g) => g.id);
            const merged = prev.messageGroups.concat(
              (resp.messageGroups || []).filter(
                (g) => !currentGroupIds.includes(g.id),
              ),
            );
            return {
              ...prev,
              messageGroups: merged,
              unreadMessageCount: {
                ...(prev.unreadMessageCount || {}),
                ...(resp.unreadMessageCount || {}),
              },
              lastMessages: {
                ...(prev.lastMessages || {}),
                ...(resp.lastMessages || {}),
              },
              pagenationSettings: resp.pagenationSettings,
            };
          });
        },
        error: () => {},
      });
    },
    [companyId],
  );

  // 初回ロード（旧 componentDidMount 相当）。setInterval は撤廃（D5）
  useEffect(() => {
    loadMessageGroupListFromServer();
  }, [loadMessageGroupListFromServer]);

  // F1〜F3: 既存スレッドの新着メッセージ
  // - 該当 group を一覧の先頭に移動
  // - lastMessages を payload で更新
  // - 自分送信でなければ unreadMessageCount を +1
  const onMessageReceived = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      const idx = prev.messageGroups.findIndex((g) => g.id === targetId);
      if (idx < 0) {
        // 一覧に存在しない既存スレッド → 次回 fetch で吸収する
        return prev;
      }
      const group = prev.messageGroups[idx];
      const reordered = [
        group,
        ...prev.messageGroups.slice(0, idx),
        ...prev.messageGroups.slice(idx + 1),
      ];

      const prevLast = (prev.lastMessages || {})[targetId] || {};
      const nextLast = {
        ...prevLast,
        body: payload.last_message_preview,
        created_at: payload.last_message_at,
        sender_id: payload.last_sender_id,
      };

      const isSelfSend = (payload.unread_count_delta || 0) === 0;
      const prevUnread = (prev.unreadMessageCount || {})[targetId] || 0;
      const nextUnread = isSelfSend
        ? prevUnread
        : prevUnread + (payload.unread_count_delta || 1);

      return {
        ...prev,
        messageGroups: reordered,
        lastMessages: { ...(prev.lastMessages || {}), [targetId]: nextLast },
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: nextUnread,
        },
      };
    });
  }, []);

  // F4: 既読化通知 → unreadMessageCount を 0 にする
  const onUnreadCountChanged = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      if (!(prev.unreadMessageCount || {})[targetId]) return prev;
      return {
        ...prev,
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: payload.unread_count || 0,
        },
      };
    });
  }, []);

  // F6: 新規スレッド作成 → 一覧の先頭に追加
  const onNewThread = useCallback((payload) => {
    setData((prev) => {
      const targetId = payload.target_id;
      // 既に存在するなら通常受信と同じ扱いに fallback
      const exists = prev.messageGroups.some((g) => g.id === targetId);
      if (exists) {
        return prev;
      }
      const group = payload.message_group;
      if (!group) {
        return prev;
      }
      const isSelfSend = (payload.unread_count_delta || 0) === 0;
      return {
        ...prev,
        messageGroups: [group, ...prev.messageGroups],
        unreadMessageCount: {
          ...(prev.unreadMessageCount || {}),
          [targetId]: isSelfSend ? 0 : payload.unread_count_delta || 1,
        },
        lastMessages: {
          ...(prev.lastMessages || {}),
          [targetId]: {
            body: payload.last_message_preview,
            created_at: payload.last_message_at,
            sender_id: payload.last_sender_id,
          },
        },
      };
    });
  }, []);

  useInboxChannel({
    currentUserId: data.currentUserId,
    companyId: typeof companyId === 'undefined' ? null : companyId,
    scope: 'dm',
    onMessageReceived,
    onUnreadCountChanged,
    onNewThread,
    refetch: loadMessageGroupListFromServer,
  });

  const currentUserId = data.currentUserId;
  const lastGroup = data.messageGroups[data.messageGroups.length - 1];
  const isLastPage = data.pagenationSettings.isLastPage;
  const currentPage = data.pagenationSettings.currentPage;

  const messageGroup = data.messageGroups.map((group) => {
    return (
      <MessageGroup
        isCompany={isCompany}
        messageGroup={group}
        currentUserId={currentUserId}
        messageGroupId={messageGroupId}
        hasUnreadMessage={false}
        key={group.id}
        isLast={lastGroup == group}
        unreadMessageCount={(data.unreadMessageCount || {})[group.id]}
        lastMessage={(data.lastMessages || {})[group.id]}
      />
    );
  });

  let messageGroupList = <p>メッセージがありません。</p>;
  const appLinkButton = isLastPage ? (
    ''
  ) : (
    <li
      onClick={() => loadMoreMessageGroupListFromServer(currentPage)}
      className="hov--default"
    >
      <div className="row justify-content-around align-items-center pt-3 pb-3 border-top">
        もっとみる
      </div>
    </li>
  );
  if (data.messageGroups) {
    messageGroupList = (
      <ul className="list-unstyled" style={{ marginBottom: '75px' }}>
        {messageGroup}
        {appLinkButton}
      </ul>
    );
  }

  return (
    <div>
      {data.messageGroups.length > 0 ? (
        <div className="container col-md-8 col-lg-6">
          <h6 className="h6--list-title text-fz-14px text-black-50">
            個人間メッセージ
          </h6>
        </div>
      ) : null}
      <div className="bg-white h-100 mb-2">
        <div className="container col-md-8 col-lg-6 h-100">
          <Loader isActive={loading} />
          {messageGroupList}
        </div>
      </div>
    </div>
  );
}

MessageGroupList.displayName = 'Root';
MessageGroupList.propTypes = {
  messageGroupId: PropTypes.number,
  isCompany: PropTypes.bool,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MessageGroupList;
